// Central API barrel. Historical entry-point for every consumer in the app.
//
// Refactor sprint (2026-02-02): Started splitting per-domain modules into
// `./api/*.js`. New domains SHOULD be created as separate files and re-exported
// here so consumers keep importing `from '@/lib/api'` unchanged. See
// `./api/_client.js` (axios instance + interceptors) and `./api/esign.js`
// (eSign domain) for the pattern. Rest of the domains stay in this file
// for now — split them as they grow.
import axios from 'axios';
import { API, API_BASE, BACKEND, TOKEN_KEY, USER_KEY, PORTAL_TOKEN_KEY, PORTAL_MATTER_KEY } from './api/_client';

export { API, API_BASE };
// Export portal-token localStorage key used by the receipt-iframe fetch
export const PORTAL_TOKEN_STORAGE_KEY = PORTAL_TOKEN_KEY;
const BACKEND_URL = BACKEND;

// Silence "axios is unused" — kept as a documented re-export slot for consumers
// who need the raw axios (rare).
export { axios };

export const auth = {
  async login(email, password) {
    const { data } = await API.post('/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    return data;
  },
  async register(payload) {
    const { data } = await API.post('/auth/register', payload);
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    return data;
  },
  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },
  currentUser() {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  },
  isAuthed() { return !!localStorage.getItem(TOKEN_KEY); },
};

export const matters = {
  list: () => API.get('/matters').then((r) => r.data),
  get: (id) => API.get(`/matters/${id}`).then((r) => r.data),
  create: (payload) => API.post('/matters', payload).then((r) => r.data),
  update: (id, payload) => API.patch(`/matters/${id}`, payload).then((r) => r.data),
  timeline: (id) => API.get(`/matters/${id}/timeline`).then((r) => r.data),
  addNote: (id, payload) => API.post(`/matters/${id}/notes`, payload).then((r) => r.data),
  addNoteWithAttachments: (id, { text, kind, files }) => {
    const fd = new FormData();
    fd.append('text', text);
    fd.append('kind', kind || 'note');
    (files || []).forEach((f) => fd.append('files', f));
    return API.post(`/matters/${id}/notes/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  attachmentUrl: (matterId, gridId) => `${API.defaults.baseURL}/matters/${matterId}/notes/attachment/${gridId}`,
};

export const trust = {
  summary: () => API.get('/trust/summary').then((r) => r.data),
  transactions: (matterId) => API.get('/trust/transactions', { params: matterId ? { matter_id: matterId } : {} }).then((r) => r.data),
  post: (payload) => API.post('/trust/transactions', payload).then((r) => r.data),
  transfer: (payload) => API.post('/trust/transfer', payload).then((r) => r.data),
  reconciliation: () => API.post('/trust/reconciliation').then((r) => r.data),
};

export const ai = {
  qualOcc: (payload) => API.post('/ai/qualification-occupation', payload).then((r) => r.data),
  rfiDraft: (payload) => API.post('/ai/rfi-draft', payload).then((r) => r.data),
};

export const vetassess = {
  list: () => API.get('/vetassess').then((r) => r.data),
  get: (id) => API.get(`/vetassess/${id}`).then((r) => r.data),
  create: (payload) => API.post('/vetassess', payload).then((r) => r.data),
  update: (id, payload) => API.patch(`/vetassess/${id}`, payload).then((r) => r.data),
  toggle: (id, idx) => API.post(`/vetassess/${id}/checklist/${idx}/toggle`).then((r) => r.data),
  stages: () => API.get('/vetassess/stages').then((r) => r.data),
};

export const lodgement = {
  prefill: (matterId) => API.get(`/lodgement/${matterId}/prefill`).then((r) => r.data),
  bundleUrl: (matterId) => {
    // Uses a short-lived query token so browser can download without headers
    const t = localStorage.getItem('immipro_token');
    return `${BACKEND_URL}/api/lodgement/${matterId}/bundle.pdf?_t=${encodeURIComponent(t || '')}`;
  },
  downloadBundle: async (matterId, filename) => {
    const res = await API.get(`/lodgement/${matterId}/bundle.pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url; a.download = filename || `ImmiPro_Bundle_${matterId}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export const documents = {
  list: (matterId) => API.get('/documents', { params: matterId ? { matter_id: matterId } : {} }).then((r) => r.data),
  upload: (matterId, file, docType) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('matter_id', matterId);
    if (docType) fd.append('doc_type', docType);
    return API.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  sign: (docId) => API.post(`/documents/${docId}/declare`).then((r) => r.data),
  remove: (docId) => API.delete(`/documents/${docId}`).then((r) => r.data),
  downloadUrl: (docId) => `${BACKEND_URL}/api/documents/${docId}/download`,
};

export const policy = {
  alerts: (params) => API.get('/policy/alerts', { params }).then((r) => r.data),
  stats: () => API.get('/policy/stats').then((r) => r.data),
  refresh: () => API.post('/policy/refresh').then((r) => r.data),
  draftComms: (alertId, tone = 'professional') => API.post(`/policy/alerts/${alertId}/draft-comms`, { tone }).then((r) => r.data),
};

export { portal, portalDocs } from './api/portal';

export const senderDomain = {
  get: () => API.get('/settings/sender-domain').then((r) => r.data),
  create: (name, region = 'us-east-1') => API.post('/settings/sender-domain', { name, region }).then((r) => r.data),
  verify: () => API.post('/settings/sender-domain/verify').then((r) => r.data),
  remove: () => API.delete('/settings/sender-domain').then((r) => r.data),
};

export { elodge } from './api/elodge';

export const deadlineNudge = {
  candidates: () => API.get('/deadlines/nudge/candidates').then((r) => r.data),
  history: (limit = 50) => API.get('/deadlines/nudge/history', { params: { limit } }).then((r) => r.data),
  run: (force = false) => API.post('/deadlines/nudge/run', { force }).then((r) => r.data),
};

export const invoices = {
  list: (matter_ref) => API.get('/invoices', { params: { matter_ref } }).then((r) => r.data),
  create: (invoice) => API.post('/invoices', invoice).then((r) => r.data),
  portalList: () => API.get('/portal/invoices').then((r) => r.data),
  portalHistory: () => API.get('/portal/invoices/history').then((r) => r.data),
  checkout: (invoice_id, origin_url) => API.post('/payments/checkout', { invoice_id, origin_url }).then((r) => r.data),
  status: (session_id) => API.get(`/payments/status/${session_id}`).then((r) => r.data),
};

export const dashboard = {
  health: () => API.get('/dashboard/health').then((r) => r.data),
};

export const cpd = {
  summary: () => API.get('/cpd/summary').then((r) => r.data),
  list: (cpd_year) => API.get('/cpd', { params: { cpd_year } }).then((r) => r.data),
  create: (entry) => API.post('/cpd', entry).then((r) => r.data),
  remove: (id) => API.delete(`/cpd/${id}`).then((r) => r.data),
  uploadEvidence: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post(`/cpd/${id}/evidence`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  evidenceUrl: (id) => `${BACKEND_URL}/api/cpd/${id}/evidence`,
  removeEvidence: (id) => API.delete(`/cpd/${id}/evidence`).then((r) => r.data),
  suggestions: (refresh = false) => API.get('/cpd/suggestions', { params: refresh ? { refresh: 1 } : {} }).then((r) => r.data),
  suggestionFeedback: (title, kind) => API.post('/cpd/suggestions/feedback', { title, kind }).then((r) => r.data),
  suggestionThemes: () => API.get('/cpd/suggestions/themes').then((r) => r.data),
  summaryPdfUrl: () => `${API.defaults.baseURL}/cpd/summary.pdf`,
};

export const alerts = {
  list: (opts = {}) => API.get('/alerts', { params: opts }).then((r) => r.data),
  markRead: (id) => API.patch(`/alerts/${id}/read`).then((r) => r.data),
  markAllRead: () => API.post('/alerts/read-all').then((r) => r.data),
  snooze: (id, hours = 24) => API.post(`/alerts/${id}/snooze`, { hours }).then((r) => r.data),
  unsnooze: (id) => API.post(`/alerts/${id}/unsnooze`).then((r) => r.data),
};

export const firmHealth = {
  history: (days = 30) => API.get('/dashboard/health/history', { params: { days } }).then((r) => r.data),
  digestPreview: () => API.get('/dashboard/weekly-digest/preview').then((r) => r.data),
  digestSend: () => API.post('/dashboard/weekly-digest/send').then((r) => r.data),
};

export const admin = {
  listUsers: (opts = {}) => API.get('/admin/users', { params: opts }).then((r) => r.data),
  listOrgs: () => API.get('/admin/orgs').then((r) => r.data),
  invite: (payload) => API.post('/admin/users/invite', payload).then((r) => r.data),
  setRole: (id, role) => API.post(`/admin/users/${id}/role`, { role }).then((r) => r.data),
  resetPassword: (id, password, sendEmail = false) =>
    API.post(`/admin/users/${id}/reset-password`, { ...(password ? { password } : {}), send_email: !!sendEmail }).then((r) => r.data),
  remove: (id) => API.delete(`/admin/users/${id}`).then((r) => r.data),
  audit: (opts = {}) => API.get('/admin/audit', { params: opts }).then((r) => r.data),
};

// Firm-scoped team management (Principal RMA + super_admin). Distinct from
// `admin` which is cross-tenant (super_admin only).
export const team = {
  members: (opts = {}) => API.get('/team/members', { params: opts }).then((r) => r.data),
  invite: (payload) => API.post('/team/invite', payload).then((r) => r.data),
  setRole: (id, role) => API.post(`/team/members/${id}/role`, { role }).then((r) => r.data),
  resetPassword: (id, password, sendEmail = false) =>
    API.post(`/team/members/${id}/reset-password`, { ...(password ? { password } : {}), send_email: !!sendEmail }).then((r) => r.data),
  remove: (id) => API.delete(`/team/members/${id}`).then((r) => r.data),
  clients: (opts = {}) => API.get('/team/clients', { params: opts }).then((r) => r.data),
};

export const analytics = {
  firm: (months = 6) => API.get('/analytics/firm', { params: { months } }).then((r) => r.data),
  drilldown: (kind, params = {}) => API.get(`/analytics/firm/drilldown/${kind}`, { params }).then((r) => r.data),
  firmPdfUrl: (months = 6) => `${API.defaults.baseURL}/analytics/firm/pdf?months=${months}`,
};

export const rounds = {
  list: (opts = {}) => API.get('/rounds', { params: opts }).then((r) => r.data),
  ingest: () => API.post('/rounds/ingest').then((r) => r.data),
};

export const quotas = {
  list: (opts = {}) => API.get('/quotas', { params: opts }).then((r) => r.data),
  current: () => API.get('/quotas/current').then((r) => r.data),
  ingest: () => API.post('/quotas/ingest').then((r) => r.data),
};

export const states = {
  list: () => API.get('/states').then((r) => r.data),
  get: (state) => API.get(`/states/${state}`).then((r) => r.data),
};

export const updates = {
  list: (opts = {}) => API.get('/updates', { params: opts }).then((r) => r.data),
  forMatter: (matterId, limit = 20) => API.get(`/updates/for-matter/${matterId}`, { params: { limit } }).then((r) => r.data),
};

export const pathways = {
  analyse: (payload) => API.post('/pathways/analyse', payload).then((r) => r.data),
  narrative: (inputs, results) => API.post('/pathways/narrative', { inputs, results }).then((r) => r.data),
  getForMatter: (matterId) => API.get(`/pathways/matter/${matterId}`).then((r) => r.data),
  pdfUrl: (matterId) => `${BACKEND_URL}/api/pathways/matter/${matterId}/pdf`,
  saveNarrative: (matterId, narrative) => API.post('/pathways/save-narrative', { matter_id: matterId, narrative }).then((r) => r.data),
};

export const intel = {
  forMatter: (matterId) => API.get(`/intel/matter/${matterId}`).then((r) => r.data),
};

export const occupations = {
  skillsAssessment: (opts = {}) => API.get('/occupations/skills-assessment', { params: opts }).then((r) => r.data),
  crossReference: (anzsco) => API.get('/occupations/cross-reference', { params: { anzsco } }).then((r) => r.data),
  anzscoList: () => API.get('/occupations/anzsco-list').then((r) => r.data),
  importCsv: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post('/occupations/anzsco-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
};

export const notifPrefs = {
  get: () => API.get('/settings/notifications').then((r) => r.data),
  set: (skillselect_alert_mode) => API.post('/settings/notifications', { skillselect_alert_mode }).then((r) => r.data),
};

export const messaging = {
  getConfig: () => API.get('/messaging/config').then((r) => r.data),
  saveConfig: (patch) => API.post('/messaging/config', patch).then((r) => r.data),
  removeConfig: () => API.delete('/messaging/config').then((r) => r.data),
  test: (to, channel, body) => API.post('/messaging/test', { to, channel, body }).then((r) => r.data),
  send: (to, channel, body, matter_ref = null) =>
    API.post('/messaging/send', { to, channel, body, matter_ref }).then((r) => r.data),
  thread: (matterRef) => API.get('/messaging/thread', { params: { matter_ref: matterRef } }).then((r) => r.data),
};

export { questions } from './api/questions';



export const branding = {
  get: () => API.get('/branding').then((r) => r.data),
  save: (patch) => API.post('/branding', patch).then((r) => r.data),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return API.post('/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  preview: () => API.get('/branding/preview').then((r) => r.data),
  // Custom email domain (Resend)
  getDomain: () => API.get('/branding/resend/domain').then((r) => r.data),
  createDomain: (domain) => API.post('/branding/resend/domain', { domain }).then((r) => r.data),
  verifyDomain: () => API.post('/branding/resend/domain/verify').then((r) => r.data),
};

// Audit reactions (thumbs-up + query flag)
export const auditReactions = {
  react: (eventId, kind, note) => API.post(`/audit/${eventId}/react`, { kind, note }).then((r) => r.data),
  forEvent: (eventId) => API.get(`/audit/${eventId}/reactions`).then((r) => r.data),
  bulk: (eventIds) => API.get('/audit/reactions', { params: { event_ids: (eventIds || []).join(',') } }).then((r) => r.data),
};

export { esign } from './api/esign';
export { tasks } from './api/tasks';
export { time } from './api/time';
export { contacts } from './api/contacts';
export { quotes } from './api/quotes';
export { reportsLibrary } from './api/reports_library';
export { intake } from './api/intake';
export { referrer } from './api/referrer';
export { workflow, anzsco, filenotesV2, points, checklist } from './api/workflow';
export { applicants, postLodgement } from './api/applicants';


export const letters = {
  templates: () => API.get('/letters/templates').then((r) => r.data),
  getTemplate: (tid) => API.get(`/letters/templates/${tid}`).then((r) => r.data),
  createTemplate: (payload) => API.post('/letters/templates', payload).then((r) => r.data),
  deleteTemplate: (tid) => API.delete(`/letters/templates/${tid}`).then((r) => r.data),
  render: (template_id, matter_ref) => API.post('/letters/render', { template_id, matter_ref }).then((r) => r.data),
  renderPdfUrl: (template_id, matter_ref) => ({ template_id, matter_ref }),
};

export const filenotes = {
  list: (matter_ref) => API.get('/filenotes', { params: { matter_ref } }).then((r) => r.data),
  create: (matter_ref, body_text, tags = []) =>
    API.post('/filenotes', { matter_ref, body_text, tags }).then((r) => r.data),
  remove: (fid) => API.delete(`/filenotes/${fid}`).then((r) => r.data),
};

export const quickparts = {
  list: () => API.get('/quickparts').then((r) => r.data),
  create: (label, snippet) => API.post('/quickparts', { label, snippet }).then((r) => r.data),
  remove: (qid) => API.delete(`/quickparts/${qid}`).then((r) => r.data),
};

export const matterArchive = {
  archive: (matter_ref) => API.post(`/matters/${matter_ref}/archive`).then((r) => r.data),
  unarchive: (matter_ref) => API.post(`/matters/${matter_ref}/unarchive`).then((r) => r.data),
};

export const reports = {
  run: (filters) => API.post('/reports/run', filters).then((r) => r.data),
  exportXlsx: (filters) => API.post('/reports/export.xlsx', filters, { responseType: 'blob' }).then((r) => r.data),
  sendMonthlyPack: (payload) => API.post('/reports/monthly-pack', payload).then((r) => r.data),
};

// First-run onboarding checklist
export const onboarding = {
  status: () => API.get('/onboarding/status').then((r) => r.data),
  dismiss: () => API.post('/onboarding/dismiss').then((r) => r.data),
  complete: () => API.post('/onboarding/complete').then((r) => r.data),
};

// Client-facing tasks (staff assigns → applicant portal ticks off)
export const applicantTasks = {
  listForMatter: (matterId) => API.get(`/matters/${matterId}/applicant-tasks`).then((r) => r.data),
  create: (matterId, payload) => API.post(`/matters/${matterId}/applicant-tasks`, payload).then((r) => r.data),
  patch: (taskId, patch) => API.patch(`/applicant-tasks/${taskId}`, patch).then((r) => r.data),
  delete: (taskId) => API.delete(`/applicant-tasks/${taskId}`).then((r) => r.data),
};

// Monthly client statements — auto + on-demand
export const statements = {
  candidates: () => API.get('/statements/candidates').then((r) => r.data),
  previewPdfUrl: (contactId) => `${API.defaults.baseURL}/statements/${contactId}.pdf`,
  send: (payload) => API.post('/statements/send', payload).then((r) => r.data),
};

// CPD — extract entry from a forwarded email
export const cpdExtract = {
  fromEmail: (emailText) => API.post('/cpd/extract-from-email', { email_text: emailText }).then((r) => r.data),
};

// SMS/WhatsApp inbox for a matter — see messaging definition above

// CPD tracker — extends existing entries CRUD with the new PDF endpoint (see cpd definition above)

export const questionnaire = {
  available: () => API.get('/questionnaire/available').then((r) => r.data),
  schema: (subclass) => API.get(`/questionnaire/schema/${subclass}`).then((r) => r.data),
  get: (matter_ref) => API.get(`/questionnaire/${matter_ref}`).then((r) => r.data),
  save: (payload) => API.post('/questionnaire/save', payload).then((r) => r.data),
};

export const portalQuestionnaire = {
  get: (matter_ref, token) =>
    API.get(`/portal/questionnaire/${matter_ref}`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data),
  save: (matter_ref, answers, submitted, token) =>
    API.post(`/portal/questionnaire/${matter_ref}`, { answers, submitted },
      { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data),
};

export const vevo = {
  check: (payload) => API.post('/vevo/check', payload).then((r) => r.data),
  batch: (matter_refs) => API.post('/vevo/batch', { matter_refs }).then((r) => r.data),
  list: (matter_ref) => API.get('/vevo/checks', { params: matter_ref ? { matter_ref } : {} }).then((r) => r.data),
};

export const xero = {
  getConfig: () => API.get('/xero/config').then((r) => r.data),
  saveConfig: (patch) => API.post('/xero/config', patch).then((r) => r.data),
  disconnect: () => API.delete('/xero/config').then((r) => r.data),
  oauthStart: () => API.get('/xero/oauth/start').then((r) => r.data),
  syncInvoice: (id) => API.post(`/xero/sync/invoice/${id}`).then((r) => r.data),
  syncTrust: (id) => API.post(`/xero/sync/trust/${id}`).then((r) => r.data),
};

export const sponsors = {
  list: () => API.get('/sponsors').then((r) => r.data),
  get: (sid) => API.get(`/sponsors/${sid}`).then((r) => r.data),
  upsert: (payload) => API.post('/sponsors', payload).then((r) => r.data),
  remove: (sid) => API.delete(`/sponsors/${sid}`).then((r) => r.data),
  batchVevo: (sid) => API.post(`/sponsors/${sid}/batch-vevo`, { dry_run: false }).then((r) => r.data),
  obligations: () => API.get('/sponsors/obligations/summary').then((r) => r.data),
};

export const xeroExtra = {
  saveWebhookKey: (webhook_key) => API.post('/xero/config/webhook-key', { webhook_key }).then((r) => r.data),
};

export const vevoSweep = {
  runNow: () => API.post('/vevo/sweep/run').then((r) => r.data),
  status: () => API.get('/vevo/sweep/status').then((r) => r.data),
};

export const sponsorsExtra = {
  suggest: (payload) => API.post('/sponsors/_suggest', payload).then((r) => r.data),
};

export const xeroExtra3 = {
  nudgeOverdue: () => API.post('/xero/reminders/nudge-overdue').then((r) => r.data),
};

export const xeroExtra2 = {
  pullOverdue: () => API.post('/xero/reminders/pull-overdue').then((r) => r.data),
};

export const reminderPrefs = {
  get: () => API.get('/xero/reminders/prefs').then((r) => r.data),
  save: (patch) => API.post('/xero/reminders/prefs', patch).then((r) => r.data),
  analytics: () => API.get('/xero/reminders/analytics').then((r) => r.data),
  preview: (payload) => API.post('/xero/reminders/preview', payload).then((r) => r.data),
  roi: () => API.get('/xero/reminders/roi').then((r) => r.data),
  roiWeek: (start) => API.get('/xero/reminders/roi/week', { params: { start } }).then((r) => r.data),
  templates: {
    get: () => API.get('/xero/reminders/templates').then((r) => r.data),
    save: (patch) => API.post('/xero/reminders/templates', patch).then((r) => r.data),
  },
};
